// TradingPortal — Main page with 8-tab navigation (Angel One Light Theme)
import React from 'react';
import { useTrading } from '../context/TradingContext';
import MarketTab from '../components/trading/tabs/MarketTab';
import WatchlistTab from '../components/trading/tabs/WatchlistTab';
import ChartTab from '../components/trading/tabs/ChartTab';
import OrdersTab from '../components/trading/tabs/OrdersTab';
import PositionsTab from '../components/trading/tabs/PositionsTab';
import OptionChainTab from '../components/trading/tabs/OptionChainTab';
import AlgoTab from '../components/trading/tabs/AlgoTab';
import JournalTab from '../components/trading/tabs/JournalTab';
import MindTab from '../components/trading/tabs/MindTab';
import SensexAlgoTab from '../components/trading/tabs/SensexAlgoTab';
import MarketStrip from '../components/trading/shared/MarketStrip';
import ConnectionStatus from '../components/trading/shared/ConnectionStatus';
import BrokerLoginPanel from '../components/trading/credentials/BrokerLoginPanel';
import OrderPanel from '../components/trading/shared/OrderPanel';
import InstrumentSearch from '../components/trading/shared/InstrumentSearch';
import EnergyBadge from '../components/trading/mind/EnergyBadge';
import IntentionBadge from '../components/trading/mind/IntentionBadge';
import PreTradeCheckModal from '../components/trading/mind/PreTradeCheckModal';
import GuardWarningModal from '../components/trading/mind/GuardWarningModal';
import CooldownModal from '../components/trading/mind/CooldownModal';
import { FaChartBar, FaListUl, FaChartLine, FaFileAlt, FaBriefcase, FaLayerGroup, FaRobot, FaBook, FaBrain, FaSync, FaSearch, FaBolt } from 'react-icons/fa';

const TABS = [
  { key: 'market', label: 'Market', icon: <FaChartBar /> },
  { key: 'watchlist', label: 'Watchlist', icon: <FaListUl /> },
  { key: 'chart', label: 'Chart', icon: <FaChartLine /> },
  { key: 'orders', label: 'Orders', icon: <FaFileAlt /> },
  { key: 'positions', label: 'Positions', icon: <FaBriefcase /> },
  { key: 'optionchain', label: 'Options', icon: <FaLayerGroup /> },
  { key: 'algo', label: 'Algo', icon: <FaRobot /> },
  { key: 'sensexalgo', label: 'SENSEX Algo', icon: <FaBolt /> },
  { key: 'journal', label: 'Journal', icon: <FaBook /> },
  { key: 'mind', label: 'Mind', icon: <FaBrain /> },
];

const TAB_COMPONENTS = {
  market: MarketTab,
  watchlist: WatchlistTab,
  chart: ChartTab,
  orders: OrdersTab,
  positions: PositionsTab,
  optionchain: OptionChainTab,
  algo: AlgoTab,
  sensexalgo: SensexAlgoTab,
  journal: JournalTab,
  mind: MindTab,
};

export default function TradingPortal() {
  const {
    activeTab, setActiveTab,
    connectionStatus,
    orderModal, setOrderModal,
    searchOpen, setSearchOpen,
    refreshPortfolio,
  } = useTrading();
  const ActiveTabComponent = TAB_COMPONENTS[activeTab] || MarketTab;
  const isConnected = connectionStatus === 'connected';

  return (
    <div className="ao-portal bg-white min-h-screen">
      {/* Blue Header Bar */}
      <div className="bg-blue-600 px-4 sm:px-6 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div>
              <h1 className="text-lg sm:text-xl font-bold text-white">Angel One Trading</h1>
              <div className="flex items-center gap-2 mt-0.5">
                <ConnectionStatus />
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {/* Mind Layer badges */}
            <EnergyBadge />
            <IntentionBadge />

            {/* Search */}
            <button
              onClick={() => setSearchOpen(true)}
              className="p-2 rounded-lg text-white/80 hover:text-white hover:bg-white/10 transition-colors"
              title="Search instruments (press /)"
            >
              <FaSearch className="text-sm" />
            </button>

            {/* Refresh Portfolio */}
            {isConnected && (
              <button
                onClick={refreshPortfolio}
                className="p-2 rounded-lg text-white/80 hover:text-white hover:bg-white/10 transition-colors"
                title="Refresh portfolio data"
              >
                <FaSync className="text-sm" />
              </button>
            )}

            {/* Login/Profile */}
            <BrokerLoginPanel />
          </div>
        </div>
      </div>

      {/* Market Strip */}
      {isConnected && (
        <div className="border-b border-gray-200 bg-gray-50">
          <MarketStrip />
        </div>
      )}

      {/* Tab Navigation — bottom border style */}
      <div className="flex items-center gap-0 border-b border-gray-200 bg-white px-2 sm:px-4 overflow-x-auto no-scrollbar">
        {TABS.map(tab => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={`flex items-center gap-1.5 px-3 sm:px-4 py-3 text-[11px] font-medium transition-all duration-200 border-b-2 whitespace-nowrap ${
              activeTab === tab.key
                ? 'bg-blue-50 text-blue-600 border-blue-600'
                : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50 border-transparent'
            }`}
          >
            <span className={`text-xs ${activeTab === tab.key ? 'text-blue-600' : 'text-gray-400'}`}>
              {tab.icon}
            </span>
            <span className="hidden sm:inline">{tab.label}</span>
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div className="p-4 sm:p-6 bg-gray-50 min-h-[calc(100vh-200px)]">
        <ActiveTabComponent />
      </div>

      {/* Order Modal */}
      {orderModal && (
        <OrderPanel
          instrument={orderModal.instrument}
          side={orderModal.side}
          onClose={() => setOrderModal(null)}
        />
      )}

      {/* Instrument Search Modal */}
      {searchOpen && (
        <InstrumentSearch onClose={() => setSearchOpen(false)} />
      )}

      {/* Mind Layer Modals */}
      <PreTradeCheckModal />
      <GuardWarningModal />
      <CooldownModal />
    </div>
  );
}
